/**
 * Hash no browser (Web Crypto/SubtleCrypto) — usado para amarrar a autorização do
 * agente local (contrato 3, `POST /v1/authorize`) ao digest real do DTBS antes de
 * pedir o nonce: o agente só assina depois de conferir que
 * `SHA-256(dtbs_b64 decodificado) == dtbs_sha256_b64` autorizado.
 */

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function bytesToBase64(bytes: Uint8Array): string {
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin);
}

/** SHA-256 de um payload Base64, devolvendo o digest também em Base64 (32 bytes). */
export async function sha256Base64(inputB64: string): Promise<string> {
  const bytes = base64ToBytes(inputB64);
  // TS enxerga Uint8Array como genérico sobre ArrayBufferLike (inclui SharedArrayBuffer), que
  // não satisfaz o `BufferSource` de SubtleCrypto.digest — `bytes` aqui é sempre um ArrayBuffer
  // comum (criado por `new Uint8Array(length)` acima, nunca uma view sobre memória compartilhada).
  const digest = await crypto.subtle.digest('SHA-256', bytes.buffer as ArrayBuffer);
  return bytesToBase64(new Uint8Array(digest));
}

/**
 * SHA-256 de um payload Base64, devolvendo o digest em hex minúsculo.
 *
 * É a mesma operação de `sha256Base64`, em outra saída — e existe porque o Open ICP
 * Bridge fala hex em dois lugares onde o ARGINK precisa CONFERIR, não confiar
 * (`lib/bridge.ts`): o `id` de um certificado é `sha256hex(DER)`, e o
 * `signedDigestSha256` da resposta de `sign` é o sha256 hex do digest que foi
 * efetivamente assinado. Sem esta função os dois gates de D-113 seriam declarações
 * do próprio Bridge sobre si mesmo, em vez de verificação independente.
 */
export async function sha256HexOfBase64(inputB64: string): Promise<string> {
  const bytes = base64ToBytes(inputB64);
  // Mesma nota de `sha256Base64`: `bytes` nasce de `new Uint8Array(length)`, nunca de
  // memória compartilhada, então o cast para `ArrayBuffer` é seguro.
  const digest = await crypto.subtle.digest('SHA-256', bytes.buffer as ArrayBuffer);
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * SHA-256 de um texto UTF-8, devolvendo o digest em hex minúsculo — o MESMO
 * formato de `sha256Hex(Buffer.from(texto, 'utf8'))` do servidor. Usado pelo
 * filtro de certificados por CPF (#92) para reproduzir o `cpf_match`.
 */
export async function sha256HexUtf8(text: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, '0'))
    .join('');
}
